import 'swiper/css'
import 'swiper/css/a11y'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import IconChevronLeft from '@fortuneo/ui-icons/general/IconChevronLeft'
import IconChevronRight from '@fortuneo/ui-icons/general/IconChevronRight'
import { ButtonIcon } from '@ui/atoms/button-icon'
import React, {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { A11y, Keyboard, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react'
import { type Swiper as SwiperClass } from 'swiper/types'

const CarouselSlide: FC<PropsWithChildren> = ({ children }) => (
  <SwiperSlide
    style={{
      display: 'flex',
      alignItems: 'stretch',
      height: 'auto',
    }}
  >
    <div className="flex h-auto w-full items-stretch p-1">{children}</div>
  </SwiperSlide>
)

const CarouselPrevButton: FC = () => {
  const swiper = useSwiper()
  return (
    <ButtonIcon
      className="carousel-button-prev"
      component="button"
      Icon={IconChevronLeft}
      onClick={() => {
        swiper.slidePrev()
      }}
    />
  )
}

const CarouselNextButton: FC = () => {
  const swiper = useSwiper()
  return (
    <ButtonIcon
      className="carousel-button-next"
      component="button"
      Icon={IconChevronRight}
      onClick={() => {
        swiper.slideNext()
      }}
    />
  )
}

const Carousel: FC<PropsWithChildren> & {
  Slide: FC<PropsWithChildren>
} = ({ children }) => {
  const [swiper, setSwiper] = useState<SwiperClass | undefined>(null)
  const [hasMultipleSlides, setHasMultipleSlides] = useState(false)
  const slidesRef = useRef<HTMLDivElement>(null)

  const onChange = useCallback((swiperInstance: SwiperClass) => {
    setSwiper(swiperInstance)
  }, [])

  useEffect(() => {
    if (swiper) {
      const totalSlides = swiper.slides.length
      const currentSlidesPerView = swiper.params.slidesPerView as number
      setHasMultipleSlides(totalSlides > currentSlidesPerView)

      swiper.update()
    }
  }, [swiper, children])

  return (
    <div ref={slidesRef}>
      <Swiper
        grabCursor={true}
        modules={[Navigation, Pagination, A11y, Keyboard]}
        rewind={true}
        spaceBetween={24}
        breakpoints={{
          768: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
          1024: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
        }}
        keyboard={{
          enabled: true,
        }}
        navigation={{
          nextEl: '.carousel-button-next',
          prevEl: '.carousel-button-prev',
          disabledClass: 'opacity-50 cursor-not-allowed',
        }}
        pagination={
          hasMultipleSlides
            ? {
                clickable: true,
                dynamicBullets: true,
              }
            : false
        }
        onSwiper={onChange}
        // onBreakpoint={() => {
        //   swiper?.update()
        // }}
      >
        {children}
        {hasMultipleSlides && (
          <div className="flex flex-row justify-between p-2">
            <CarouselPrevButton />
            <CarouselNextButton />
          </div>
        )}
      </Swiper>
    </div>
  )
}

CarouselSlide.displayName = 'SwiperSlide'
Carousel.Slide = CarouselSlide

export default Carousel
